import type { PageContent, PageTheme, SectionType } from '../model/pages';
import { LIGHT_STYLES } from '../render/styles';

// ── Template thumbnail ─────────────────────────────────────────────────
// A tiny CSS skeleton of a page: each section type maps to a small block
// shape (hero = tall block with centered lines, pricing = columns, gallery =
// dots…). Pure divs. Dark/bold styles get a dark thumb background.
// Shown by the Pages list cards and the New page gallery until the live
// preview (TemplatePreview) scrolls into view.

function ThumbBlock({ type, line, soft, brand }: { type: SectionType; line: string; soft: string; brand: string }) {
  switch (type) {
    case 'hero':
      return (
        <div className={`grid h-7 content-center justify-items-center gap-1 rounded ${soft}`}>
          <div className={`h-1.5 w-2/3 rounded-full ${line}`} />
          <div className={`h-1 w-1/2 rounded-full ${line} opacity-60`} />
        </div>
      );
    case 'pricing': case 'features': case 'stats': case 'steps':
      return (
        <div className="grid grid-cols-3 gap-1">
          {[0, 1, 2].map((i) => <div key={i} className={`rounded ${soft} ${type === 'pricing' ? 'h-5' : 'h-3'}`}
            style={type === 'pricing' && i === 1 ? { boxShadow: `inset 0 0 0 1px ${brand}` } : undefined} />)}
        </div>
      );
    case 'gallery': case 'logos': case 'team':
      return (
        <div className="flex justify-center gap-1">
          {[0, 1, 2, 3].map((i) => <div key={i} className={`h-2.5 w-2.5 ${type === 'gallery' ? 'rounded-sm' : 'rounded-full'} ${soft}`} />)}
        </div>
      );
    case 'compare':
      return (
        <div className="grid grid-cols-2 gap-1">
          <div className={`h-5 rounded ${soft}`} />
          <div className={`h-5 rounded ${soft}`} style={{ boxShadow: `inset 0 0 0 1px ${brand}` }} />
        </div>
      );
    case 'cta':
      return <div className="mx-auto h-2 w-1/3 rounded-full" style={{ background: brand }} />;
    case 'form': case 'booking':
      return (
        <div className={`grid gap-0.5 rounded p-1 ${soft}`}>
          <div className={`h-1 rounded-full ${line}`} />
          <div className="h-1 w-1/2 rounded-full" style={{ background: brand }} />
        </div>
      );
    case 'video': case 'image': case 'embed':
      return (
        <div className={`grid h-4 place-items-center rounded ${soft}`}>
          <div className={`h-1.5 w-1.5 rounded-full ${line}`} />
        </div>
      );
    case 'testimonial':
      return (
        <div className="flex items-center gap-1">
          <div className={`h-2.5 w-2.5 shrink-0 rounded-full ${soft}`} />
          <div className={`h-1.5 flex-1 rounded-full ${soft}`} />
        </div>
      );
    case 'split':
      return (
        <div className="flex items-center gap-1">
          <div className={`h-5 w-6 shrink-0 rounded ${soft}`} />
          <div className="grid flex-1 gap-0.5">
            <div className={`h-1 rounded-full ${line}`} />
            <div className={`h-1 w-3/4 rounded-full ${soft}`} />
          </div>
        </div>
      );
    case 'countdown':
      return (
        <div className="flex justify-center gap-0.5">
          {[0, 1, 2, 3].map((i) => <div key={i} className={`h-2 w-3 rounded-sm ${soft}`} />)}
        </div>
      );
    case 'divider':
      return <div className={`mx-auto h-px w-2/3 ${soft}`} />;
    case 'footer':
      return <div className={`mx-auto h-1 w-1/2 rounded-full ${soft}`} />;
    default: // text, benefits, faq, guarantee…
      return (
        <div className="grid gap-0.5">
          <div className={`h-1 rounded-full ${soft}`} />
          <div className={`h-1 w-3/4 rounded-full ${soft}`} />
        </div>
      );
  }
}

/** The sections a thumbnail draws: the first step's (content.sections mirrors
 *  it on saved pages; a page with only steps still gets a thumbnail). */
function firstSections(content: PageContent) {
  if (content.sections?.length) return content.sections;
  return content.steps?.[0]?.sections ?? [];
}

export default function TemplateThumb({ content, theme }: { content: PageContent; theme: PageTheme }) {
  const dark = !LIGHT_STYLES.includes(theme.style ?? 'light');
  const soft = dark ? 'bg-white/15' : 'bg-slate-200';
  const line = dark ? 'bg-white/35' : 'bg-slate-400/70';
  const brand = theme.brand_color ?? '#4f46e5';
  const sections = firstSections(content ?? {}).filter((s) => !s.hidden).slice(0, 6);
  return (
    <div aria-hidden className={`grid h-full min-h-[90px] content-start gap-1 overflow-hidden p-1.5 ${dark ? 'bg-slate-900' : 'bg-white'}`}>
      {sections.map((sec) => <ThumbBlock key={sec.id} type={sec.type} line={line} soft={soft} brand={brand} />)}
    </div>
  );
}
